import { useQuery } from '@tanstack/react-query'
import { SectionCard, DataTable, formatDate, type Column } from '@datahub/design-system'
import type { EntityType } from '@datahub/shared'
import { get } from '../../lib/api'
import { plainText } from '../../lib/prose'

export interface CoreTransaction {
  serialNo: string | null
  title: string
  announcementDate: string | null
  currency: string | null
  amount: number | null
  /** A fraction as Core stores it: 0.5335 is a 53.35% stake. */
  equityStake: number | null
  exitDate: string | null
  announcementLink: string | null
  fund: string | null
  /** Core's summary of the deal; only about half of a fund's transactions carry one. */
  summary: string | null
  company: { name: string; slug: string | null } | null
  types: string[]
  instruments: string[]
  investors: { name: string; slug: string | null }[]
  leadInvestors: string[]
  sellers: string[]
}

interface TransactionsResponse {
  linked: boolean
  total: number
  hasMore: boolean
  items: CoreTransaction[]
}

const PAGE = 100

/** Core's transaction type names carry no space before the bracket; the table needs one. */
function typeLabel(types: string[]): string {
  return types.map((t) => t.replace(/(\S)\(/, '$1 (')).join(', ') || '-'
}

function amountLabel(t: CoreTransaction): string {
  if (t.amount === null) return '-'
  const figure = t.amount.toLocaleString('en-GB', { maximumFractionDigits: 2 })
  return t.currency ? `${t.currency} ${figure}` : figure
}

/** Core stores the stake as a fraction, so 0.5335 has to read as 53.4%. */
function stakeLabel(stake: number | null): string {
  if (stake === null) return '-'
  return `${(stake * 100).toLocaleString('en-GB', { maximumFractionDigits: 1 })}%`
}

/**
 * The transactions Stears records against this entity, matching what the platform shows:
 * for a company the deals it was the target of, for a fund manager or limited partner the
 * deals it invested in.
 *
 * Read live rather than from the stored profile. A deal is announced without anything
 * about the entity's own record changing, so a value captured at pull time would be stale
 * by the next announcement, and an admin checking what Stears holds would be misled by it.
 */
export function StearsTransactionsCard({
  entityId,
  entityType,
  basePath,
}: {
  entityId: string
  entityType: EntityType
  basePath: string
}) {
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['entity-transactions', entityId],
    queryFn: () => get<TransactionsResponse>(`${basePath}/${entityId}/stears/transactions?limit=${PAGE}`),
    // Core is a third party on the far side of a login; one retry, then say so.
    retry: 1,
  })

  if (isLoading) {
    return (
      <SectionCard title="Transactions on Stears">
        <p className="font-mulish text-text-support" style={{ fontSize: 12, lineHeight: '22px', padding: '12px 20px' }}>
          Reading transactions from Stears…
        </p>
      </SectionCard>
    )
  }

  if (isError) {
    return (
      <SectionCard title="Transactions on Stears">
        <p className="font-mulish text-text-support" style={{ fontSize: 12, lineHeight: '22px', padding: '12px 20px' }}>
          Stears could not be reached for transactions{error instanceof Error ? `: ${error.message}` : '.'} Everything
          else on this page is unaffected.
        </p>
      </SectionCard>
    )
  }

  // Not linked to Stears is already said once at the top of the page; saying it again here
  // adds an empty card to every unlinked entity for no gain.
  if (!data || !data.linked) return null

  const isFund = entityType !== 'company'
  const subject = isFund ? 'it invested in' : 'it was the target of'

  const columns: Column<CoreTransaction>[] = [
    {
      key: 'date',
      header: 'Date',
      width: 110,
      sticky: true,
      render: (t) => (t.announcementDate ? formatDate(t.announcementDate) : '-'),
    },
    {
      key: 'title',
      header: 'Transaction',
      width: 360,
      render: (t) =>
        t.announcementLink ? (
          <a
            href={t.announcementLink}
            target="_blank"
            rel="noopener noreferrer"
            title={t.title}
            className="text-text-hyperlink hover:underline"
          >
            {t.title}
          </a>
        ) : (
          t.title
        ),
    },
    {
      key: 'summary',
      header: 'Description',
      width: 360,
      // Core's summary runs to a thousand characters and is written in Markdown; the cell
      // clips the plain text and the hover carries the whole of it. Deals recorded before
      // Core had the field simply have none.
      render: (t) => {
        const summary = plainText(t.summary)
        return summary ? <span title={summary}>{summary}</span> : '-'
      },
    },
    { key: 'type', header: 'Type', width: 180, render: (t) => typeLabel(t.types) },
    { key: 'company', header: 'Company', width: 220, render: (t) => t.company?.name ?? '-' },
    {
      key: 'investors',
      header: 'Investors',
      width: 240,
      render: (t) => t.investors.map((i) => i.name).join(', ') || '-',
    },
    { key: 'amount', header: 'Amount', width: 160, align: 'right', render: amountLabel },
    { key: 'stake', header: 'Stake', width: 90, align: 'right', render: (t) => stakeLabel(t.equityStake) },
  ]

  return (
    <SectionCard title={`Transactions on Stears (${data.total})`}>
      <div className="flex flex-col">
        <p
          className="font-mulish text-text-support"
          style={{ fontSize: 10, lineHeight: '15px', padding: '8px 20px', borderBottom: '0.5px solid #4B5563' }}
        >
          What Stears records against this {isFund ? 'investor' : 'company'} — the transactions {subject}, newest first.
          Read from Stears as this page opened, not stored here.
          {data.hasMore ? ` Showing the most recent ${data.items.length} of ${data.total}.` : ''}
        </p>
        <DataTable
          columns={columns}
          rows={data.items}
          rowKey={(t) => t.serialNo ?? `${t.announcementDate ?? ''}-${t.title}`}
          caption="Transactions on Stears"
          emptyMessage="Stears records no transactions against this entity"
          maxHeight="520px"
        />
      </div>
    </SectionCard>
  )
}
